import { useProcessDocumentMutation } from '@common-reducers/backend-api/backend-api';
import {
  DBKnowledgeBaseDocumentThunks,
  DBKnowledgeBaseThunks,
  knowledgeBaseDocumentSelectByQuery,
  knowledgeBaseSelectOneObjectByQuery
} from '@common-reducers/DBServiceThunks';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { KnowledgeBaseDocument } from '@common/models/knowledge-base-document';
import { selectKnowledgeBaseTotalSize } from '@common/reducers/KnowledgeBaseSelectors';
import { formatDistanceToNow, type Locale } from 'date-fns';
import { arSA, de, enUS, es, fr, he, hi, it, pt } from 'date-fns/locale';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TestQueryDialog from '../TestQueryDialog';
import './knowledge-base-detail.module.css';
import { cn } from '@/lib/utils';
import { getDocumentTypeFromExtension, getAcceptString } from '@common/types/document-types';
import { AnalyticsService } from '@common/services/analytics-service';
import { reportAgentEvent } from '@common/services/agent-analytics';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';

// Shadcn UI Components
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

// Lucide Icons
import {
  AlertCircle,
  BrainCircuit,
  CheckCircle2,
  ChevronLeft,
  Download,
  FileText,
  MoreVertical,
  Pencil,
  RefreshCw,
  Trash2,
  Upload
} from 'lucide-react';

const DATE_FNS_LOCALE_BY_LANG: Record<string, Locale> = {
  ar: arSA,
  de,
  en: enUS,
  es,
  fr,
  he,
  hi,
  it,
  pt,
};

// Types
interface Document {
  id: string;
  name: string;
  type: 'pdf' | 'word' | 'excel' | 'text' | 'html';
  size: number;
  uploadedAt: string;
  status: 'completed' | 'processing' | 'error';
  chunks?: number;
  progress?: number;
  error?: string;
}

// Utility function to format bytes into human readable format
const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

const getStatusIcon = (status: Document['status'] | 'failed') => {
  switch(status) {
    case 'completed':
      return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
    case 'processing':
      return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
    case 'error':
    case 'failed':
      return <AlertCircle className="w-4 h-4 text-red-500" />;
    default:
      return null;
  }
};

const KnowledgeBaseDetailContent = () => {
  const { t, i18n } = useTranslation(namespace);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const analytics = useMemo(() => new AnalyticsService(), []);
  const isRtl = i18n.dir(i18n.language) === 'rtl';
  const relativeDateLocale = DATE_FNS_LOCALE_BY_LANG[i18n.language.split('-')[0].toLowerCase()] || enUS;
  
  // State
  const [docTypeFilter, setDocTypeFilter] = useState('all');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [testQueryDialogOpen, setTestQueryDialogOpen] = useState(false);
  const [editNameDialogOpen, setEditNameDialogOpen] = useState(false);
  const [editDescriptionDialogOpen, setEditDescriptionDialogOpen] = useState(false);
  const [newKnowledgeBaseName, setNewKnowledgeBaseName] = useState('');
  const [newKnowledgeBaseDescription, setNewKnowledgeBaseDescription] = useState('');
  const [currentTestDocument, setCurrentTestDocument] = useState<KnowledgeBaseDocument | null>(null);
  const [deletingDocumentId, setDeletingDocumentId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  
  // API hooks
  const [processDocument] = useProcessDocumentMutation();

  useEffect(() => {
    let isMounted = true;
    if (id) {
      Promise.all([
        dispatch(DBKnowledgeBaseThunks.find({ _id: id })),
        dispatch(DBKnowledgeBaseDocumentThunks.find({ knowledgeBaseId: id }))
      ]).finally(() => {
        if (isMounted) {
          setIsInitialLoading(false);
        }
      });
    }
    return () => {
      isMounted = false;
    };
  }, [dispatch, id]);

  const knowledgeBase = useAppSelector(state => 
    id ? knowledgeBaseSelectOneObjectByQuery(state.DBKnowledgeBaseReducer, { _id: id }) : null
  );

  const documents = useAppSelector(state => {
    if (!id) return [];
    const query = {
      knowledgeBaseId: id,
      ...(docTypeFilter !== 'all' && { type: docTypeFilter })
    };
    return knowledgeBaseDocumentSelectByQuery(state.DBKnowledgeBaseDocumentReducer, query);
  });

  // Get the total size of documents for this knowledge base
  const totalSize = useAppSelector(state => 
    id ? selectKnowledgeBaseTotalSize(state, id) : 0
  );

  const handleBack = () => {
    navigate('/knowledge-base');
  };

  const handleDocTypeChange = (value: string) => {
    setDocTypeFilter(value);
  };

  const handleTestQuery = (document: KnowledgeBaseDocument) => {
    setCurrentTestDocument(document);
    setTestQueryDialogOpen(true);
  };

  const handleDownloadDocument = async (documentId: string, documentName: string) => {
    try {
      // TODO: Implement document download
      // const response = await dispatch(DBKnowledgeBaseDocumentThunks.download(documentId)).unwrap();
      // window.open(response.downloadUrl, '_blank');
      toast.success(t('knowledgeBase.detail.toasts.downloadStartedTitle'), {
        description: t('knowledgeBase.detail.toasts.downloadStartedDescription', { name: documentName }),
      });
    } catch (error) {
      console.error('Failed to download document:', error);
      toast.error(t('knowledgeBase.detail.toasts.downloadFailedTitle'), {
        description: t('knowledgeBase.detail.toasts.downloadFailedDescription'),
      });
    }
  };

  const handleDeleteDocument = async (documentId: string, documentName: string) => {
    // If already deleting this document, prevent multiple deletion attempts
    if (deletingDocumentId === documentId) return;
    
    // Set the deleting document ID to show loading state
    setDeletingDocumentId(documentId);
    
    try {
      await dispatch(DBKnowledgeBaseDocumentThunks.delete({ 
        entity: { _id: documentId },
      }))
      
      // Refresh documents list
      if (id) {
        dispatch(DBKnowledgeBaseDocumentThunks.find({ knowledgeBaseId: id }));
        // Also refresh the knowledge base to update stats
        dispatch(DBKnowledgeBaseThunks.find({ _id: id }));
      }
      
      toast.success(t('knowledgeBase.detail.toasts.documentDeletedTitle'), {
        description: t('knowledgeBase.detail.toasts.documentDeletedDescription', { name: documentName }),
      });
    } catch (error) {
      console.error('Failed to delete document:', error);
      toast.error(t('knowledgeBase.detail.toasts.documentDeleteFailedTitle'), {
        description: t('knowledgeBase.detail.toasts.documentDeleteFailedDescription'),
      });
    } finally {
      // Clear the deleting document ID
      setDeletingDocumentId(null);
    }
  };

  const handleUploadDialogOpen = () => {
    setUploadDialogOpen(true);
  };

  const handleUploadDialogClose = () => {
    setUploadDialogOpen(false);
    setSelectedFile(null);
    setUploadProgress(0);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUploadDocument = async () => {
    if (!selectedFile || !id) return;

    // Validate file size (max 10MB)
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
    if (selectedFile.size > MAX_FILE_SIZE) {
      toast.error(t('knowledgeBase.detail.toasts.uploadFailedTitle'), {
        description: t('knowledgeBase.detail.toasts.fileTooLargeDescription'),
      });
      return;
    }

    try {
      setUploadProgress(1);

      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const base64Content = (e.target?.result as string).split(',')[1];
          const fileType = selectedFile.name.split('.').pop()?.toLowerCase() || '';
          
          const documentType = getDocumentTypeFromExtension(fileType);
          if (!documentType) {
            throw new Error(t('knowledgeBase.detail.toasts.unsupportedFileType', { type: fileType }));
          }

          // Process the document content for chunking and embeddings
          await processDocument({
            knowledgeBaseId: id,
            documentContent: base64Content,
            documentType,
            documentName: selectedFile.name,
            metadata: {
              mimeType: selectedFile.type,
              createdAt: new Date().toISOString()
            }
          }).unwrap();

          toast.success(t('knowledgeBase.detail.toasts.uploadSuccessTitle'), {
            description: t('knowledgeBase.detail.toasts.uploadSuccessDescription'),
          });
          
          // No need to refresh documents list as the backend notifier will handle this
          handleUploadDialogClose();
        } catch (error: any) {
          console.error('Failed to process document:', error);
          toast.error(t('knowledgeBase.detail.toasts.processingFailedTitle'), {
            description: error.message || t('knowledgeBase.detail.toasts.processingFailedDescription'),
          });
        } finally {
          setUploadProgress(0);
        }
      };

      reader.onerror = () => {
        toast.error(t('knowledgeBase.detail.toasts.uploadFailedTitle'), {
          description: t('knowledgeBase.detail.toasts.readFileFailedDescription'),
        });
        setUploadProgress(0);
      };

      reader.readAsDataURL(selectedFile);
    } catch (error: any) {
      console.error('Failed to upload document:', error);
      toast.error(t('knowledgeBase.detail.toasts.uploadFailedTitle'), {
        description: error.message || t('knowledgeBase.detail.toasts.uploadFailedDescription'),
      });
      setUploadProgress(0);
    }
  };

  const handleEditName = () => {
    if (knowledgeBase) {
      setNewKnowledgeBaseName(knowledgeBase.name);
      setEditNameDialogOpen(true);
    }
  };

  const handleSaveName = async () => {
    if (!id || !newKnowledgeBaseName.trim()) return;

    try {
      await dispatch(DBKnowledgeBaseThunks.patch({
        entity: {
          _id: id,
          name: newKnowledgeBaseName.trim()
        }
      })).unwrap();
      
      // Refresh knowledge base data
      dispatch(DBKnowledgeBaseThunks.find({ _id: id }));
      
      toast.success(t('knowledgeBase.detail.toasts.nameUpdatedTitle'), {
        description: t('knowledgeBase.detail.toasts.nameUpdatedDescription'),
      });
      
      setEditNameDialogOpen(false);
    } catch (error: any) {
      console.error('Failed to update knowledge base name:', error);
      toast.error(t('knowledgeBase.detail.toasts.updateFailedTitle'), {
        description: error.message || t('knowledgeBase.detail.toasts.nameUpdateFailedDescription'),
      });
    }
  };

  const handleEditDescription = () => {
    if (knowledgeBase) {
      setNewKnowledgeBaseDescription(knowledgeBase.description || '');
      setEditDescriptionDialogOpen(true);
    }
  };

  const handleSaveDescription = async () => {
    if (!id) return;

    try {
      await dispatch(DBKnowledgeBaseThunks.patch({
        entity: {
          _id: id,
          description: newKnowledgeBaseDescription.trim()
        }
      })).unwrap();
      
      // Refresh knowledge base data
      dispatch(DBKnowledgeBaseThunks.find({ _id: id }));
      
      toast.success(t('knowledgeBase.detail.toasts.descriptionUpdatedTitle'), {
        description: t('knowledgeBase.detail.toasts.descriptionUpdatedDescription'),
      });
      
      setEditDescriptionDialogOpen(false);
    } catch (error: any) {
      console.error('Failed to update knowledge base description:', error);
      toast.error(t('knowledgeBase.detail.toasts.updateFailedTitle'), {
        description: error.message || t('knowledgeBase.detail.toasts.descriptionUpdateFailedDescription'),
      });
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0 || !id) return;

    // Track document upload attempt
    analytics.event('kb_document_upload_initiated', {
      category: 'knowledge_base_documents',
      action: 'document_upload_started',
      kb_id: id,
      kb_name: knowledgeBase?.name,
      file_count: files.length,
      upload_method: 'drag_drop'
    });

    // Process each dropped file
    for (const file of files) {
      // Validate file size (max 10MB)
      const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
      if (file.size > MAX_FILE_SIZE) {
        toast.error(t('knowledgeBase.detail.toasts.uploadFailedTitle'), {
          description: t('knowledgeBase.detail.toasts.fileExceedsLimitDescription', { name: file.name }),
        });
        continue;
      }

      try {
        setUploadProgress(1);
        const reader = new FileReader();
        
        reader.onload = async (e) => {
          try {
            const base64Content = (e.target?.result as string).split(',')[1];
            const fileType = file.name.split('.').pop()?.toLowerCase() || '';
            
            const documentType = getDocumentTypeFromExtension(fileType);
            if (!documentType) {
              throw new Error(t('knowledgeBase.detail.toasts.unsupportedFileType', { type: fileType }));
            }

            // Process the document content for chunking and embeddings
            await processDocument({
              knowledgeBaseId: id,
              documentContent: base64Content,
              documentType,
              documentName: file.name,
              metadata: {
                mimeType: file.type,
                createdAt: new Date().toISOString()
              }
            }).unwrap();

            // Track successful document upload
            analytics.event('kb_document_upload_success', {
              category: 'knowledge_base_documents',
              action: 'document_uploaded',
              kb_id: id,
              kb_name: knowledgeBase?.name,
              document_name: file.name,
              document_type: documentType,
              file_size_bytes: file.size,
              upload_method: 'drag_drop'
            });
            // Unified KB-upload signal (kb_document_upload_success also mirrors to the bus).
            reportAgentEvent('agent_knowledge_uploaded', 'gemini', { agentId: id, document_type: documentType });

            toast.success(t('knowledgeBase.detail.toasts.uploadSuccessTitle'), {
              description: t('knowledgeBase.detail.toasts.fileUploadedSuccessDescription', { name: file.name }),
            });
            
            // No need to refresh documents list as the backend notifier will handle this
          } catch (error: any) {
            console.error('Failed to process document:', error);
            
            // Track document upload failure
            analytics.event('kb_document_upload_error', {
              category: 'knowledge_base_documents',
              action: 'document_upload_failed',
              kb_id: id,
              kb_name: knowledgeBase?.name,
              document_name: file.name,
              file_size_bytes: file.size,
              upload_method: 'drag_drop',
              error: error.message || t('knowledgeBase.unknown')
            });
            
            toast.error(t('knowledgeBase.detail.toasts.processingFailedTitle'), {
              description: t('knowledgeBase.detail.toasts.fileProcessingFailedDescription', {
                name: file.name,
                error: error.message || t('knowledgeBase.unknown')
              }),
            });
          }
        };

        reader.onerror = () => {
          toast.error(t('knowledgeBase.detail.toasts.uploadFailedTitle'), {
            description: t('knowledgeBase.detail.toasts.fileReadFailedDescription', { name: file.name }),
          });
        };

        reader.readAsDataURL(file);
      } catch (error: any) {
        console.error('Failed to upload document:', error);
        toast.error(t('knowledgeBase.detail.toasts.uploadFailedTitle'), {
          description: t('knowledgeBase.detail.toasts.fileUploadFailedDescription', {
            name: file.name,
            error: error.message || t('knowledgeBase.unknown')
          }),
        });
      }
    }
    setUploadProgress(0);
  };

  if (isInitialLoading || !knowledgeBase) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-bg)' }}>
        <div style={{ color: 'var(--color-text-muted)' }}>{t('knowledgeBase.detail.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-bg)' }}>
      <div className="p-4 md:p-8">
        {/* Header */}
        <div 
          className="flex items-center gap-2 text-sm mb-6 cursor-pointer transition-colors"
          style={{ color: 'var(--color-text-muted)' }}
          onClick={handleBack}
        >
          <ChevronLeft className={cn('w-4 h-4', isRtl && 'rotate-180')} />
          <span>{t('knowledgeBase.detail.backToKnowledgeBases')}</span>
        </div>

        <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4 mb-8">
          <div className="min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-semibold" style={{ color: 'var(--color-text)' }}>{knowledgeBase.name}</h1>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8"
                onClick={handleEditName}
              >
                <Pencil className="w-4 h-4 text-gray-400" />
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{knowledgeBase.description || t('knowledgeBase.detail.noDescription')}</p>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-6 w-6"
                onClick={handleEditDescription}
              >
                <Pencil className="w-3 h-3 text-gray-400" />
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <Button
              variant="outline"
              className="flex items-center gap-2"
              style={{ color: 'var(--color-text)' }}
            >
              <Download className="w-4 h-4" />
              {t('knowledgeBase.detail.export')}
            </Button>
            <Button 
              className="flex items-center gap-2"
              style={{ backgroundColor: 'var(--fab-solid-bg)', color: 'var(--fab-text)' }}
              onClick={handleUploadDialogOpen}
            >
              <Upload className="w-4 h-4" />
              {t('knowledgeBase.detail.uploadDocuments')}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="md:col-span-2">
            {/* Document List */}
            <Card className="shadow-sm" style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                <CardTitle className="text-lg font-semibold">{t('knowledgeBase.documents')}</CardTitle>
                <Select 
                  defaultValue={docTypeFilter} 
                  onValueChange={handleDocTypeChange}
                >
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder={t('knowledgeBase.detail.filters.allTypes')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('knowledgeBase.detail.filters.allTypes')}</SelectItem>
                    <SelectItem value="pdf">{t('knowledgeBase.detail.filters.pdf')}</SelectItem>
                    <SelectItem value="word">{t('knowledgeBase.detail.filters.word')}</SelectItem>
                    <SelectItem value="text">{t('knowledgeBase.detail.filters.text')}</SelectItem>
                    <SelectItem value="html">{t('knowledgeBase.detail.filters.html')}</SelectItem>
                  </SelectContent>
                </Select>
              </CardHeader>
              <CardContent className="p-0">
                <div 
                  className={cn(
                    "flex-1 p-4 relative",
                    isDragging && "border-2 border-dashed rounded-lg"
                  )}
                  style={{ backgroundColor: 'var(--color-surface)', borderColor: isDragging ? 'var(--color-accent)' : 'var(--color-border)' }}
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  {isDragging && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-lg font-medium" style={{ color: 'var(--color-link)' }}>
                        {t('knowledgeBase.detail.dropToUpload')}
                      </div>
                    </div>
                  )}
                  <ScrollArea className="max-h-[60vh] relative">
                    {uploadProgress > 0 && (
                      <div className="absolute inset-x-0 top-0 z-10 p-4" style={{ backgroundColor: 'color-mix(in oklab, var(--color-surface) 80%, transparent)' }}>
                        <Progress value={uploadProgress} className="h-2" />
                        <p className="text-center text-sm mt-2" style={{ color: 'var(--color-text-muted)' }}>
                          {t('knowledgeBase.detail.processingDocument')}
                        </p>
                      </div>
                    )}
                    <div className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
                      {documents.length === 0 ? (
                        <div 
                          className="p-8 text-center cursor-pointer rounded-lg transition-colors"
                          style={{ color: 'var(--color-text-muted)' }}
                          onClick={handleUploadDialogOpen}
                        >
                          <FileText className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-text-muted)' }} />
                          <p>{t('knowledgeBase.detail.noDocumentsFound')}</p>
                          <p className="text-sm mt-2">{t('knowledgeBase.detail.clickOrDragToUpload')}</p>
                        </div>
                      ) : (
                        documents.map(doc => {
                          const isProcessing = doc.status === 'processing';
                          
                          return (
                            <div 
                              key={doc._id} 
                              className={`flex items-center justify-between p-4`}
                              style={{ backgroundColor: deletingDocumentId === doc._id ? 'color-mix(in oklab, var(--color-surface) 90%, transparent)' : 'var(--color-surface)' }}
                            >
                              <div className="flex items-center gap-3">
                                <FileText className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
                                <div>
                                  <div className="flex items-center gap-2">
                                    <p className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>{doc.name}</p>
                                    {deletingDocumentId === doc._id && (
                                      <span className="text-xs animate-pulse" style={{ color: 'var(--color-danger)' }}>{t('knowledgeBase.detail.deleting')}</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2 mt-1">
                                    <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                                      {formatBytes(doc.metadata?.contentLength || 0)}
                                    </span>
                                    <span style={{ color: 'var(--color-text-muted)' }}>•</span>
                                    <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                                      {doc.createdAt
                                        ? formatDistanceToNow(new Date(doc.createdAt), { addSuffix: true, locale: relativeDateLocale })
                                        : t('knowledgeBase.unknown')}
                                    </span>
                                    {isProcessing && doc.progress !== undefined && (
                                      <>
                                        <span style={{ color: 'var(--color-text-muted)' }}>•</span>
                                        <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                                          {doc.processingStage}: {doc.progress}%
                                          {doc.currentChunk !== undefined && doc.totalChunks !== undefined && (
                                            ` (${t('knowledgeBase.detail.chunkProgress', { current: doc.currentChunk, total: doc.totalChunks })})`
                                          )}
                                        </span>
                                      </>
                                    )}
                                    {doc.status === 'completed' && doc.totalChunks && (
                                      <>
                                        <span style={{ color: 'var(--color-text-muted)' }}>•</span>
                                        <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                                          {t('knowledgeBase.detail.chunkCount', { count: doc.totalChunks })}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-4">
                                <Badge style={{
                                  backgroundColor: doc.status === 'completed' ? 'var(--tasks-group-bg)' : doc.status === 'processing' ? 'var(--color-surface-accent)' : 'color-mix(in oklab, var(--color-danger) 20%, transparent)',
                                  color: doc.status === 'completed' ? 'var(--color-text)' : doc.status === 'processing' ? 'var(--color-text)' : 'var(--color-danger)'
                                }}>
                                  <div className="flex items-center gap-1">
                                    {getStatusIcon(doc.status)}
                                    <span>{t(`knowledgeBase.detail.status.${doc.status}`, { defaultValue: doc.status })}</span>
                                  </div>
                                </Badge>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button 
                                      variant="ghost" 
                                      size="icon" 
                                      className="h-8 w-8"
                                      disabled={deletingDocumentId === doc._id}
                                    >
                                      {deletingDocumentId === doc._id ? (
                                        <RefreshCw className="w-4 h-4 animate-spin" style={{ color: 'var(--color-text-muted)' }} />
                                      ) : (
                                        <MoreVertical className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
                                      )}
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem 
                                      onClick={() => {
                                        handleTestQuery(doc);
                                      }}
                                      disabled={deletingDocumentId === doc._id}
                                    >
                                      <BrainCircuit className="w-4 h-4" />
                                      {t('knowledgeBase.detail.testQuery')}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem 
                                      style={{ color: 'var(--color-danger)' }}
                                      onClick={() => {
                                        handleDeleteDocument(doc._id ?? '', doc.name ?? '');
                                      }}
                                      disabled={deletingDocumentId === doc._id}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                      {t('knowledgeBase.delete')}
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </ScrollArea>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* KB Info */}
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">{t('knowledgeBase.detail.details')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500">{t('knowledgeBase.detail.created')}</p>
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(knowledgeBase.createdAt || '').toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('knowledgeBase.detail.totalDocuments')}</p>
                    <p className="text-sm font-medium text-gray-900">
                      {knowledgeBase.documentCount || documents.length}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('knowledgeBase.totalSize')}</p>
                    <p className="text-sm font-medium text-gray-900">
                      {formatBytes(totalSize)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('knowledgeBase.detail.dialogs.upload.title')}</DialogTitle>
            <DialogDescription>
              {t('knowledgeBase.detail.dialogs.upload.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <input
              type="file"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id="file-upload-input"
              accept={getAcceptString()}
            />
            <label htmlFor="file-upload-input">
              <Button
                variant="outline"
                className="w-full justify-center"
                asChild
              >
                <span className="flex items-center gap-2">
                  <Upload className="w-4 h-4" />
                  {selectedFile ? selectedFile.name : t('knowledgeBase.detail.dialogs.upload.chooseFile')}
                </span>
              </Button>
            </label>
            {selectedFile && (
              <p className="text-xs text-gray-500 mt-2">
                {t('knowledgeBase.detail.dialogs.upload.fileSize', { size: formatBytes(selectedFile.size) })}
              </p>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button 
              variant="outline" 
              onClick={handleUploadDialogClose}
            >
              {t('knowledgeBase.cancel')}
            </Button>
            <Button 
              onClick={handleUploadDocument}
              disabled={!selectedFile || uploadProgress > 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {uploadProgress > 0 ? t('knowledgeBase.detail.processing') : t('knowledgeBase.detail.upload')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Query Dialog */}
      {currentTestDocument && (
        <TestQueryDialog 
          open={testQueryDialogOpen} 
          onOpenChange={(open) => {
            setTestQueryDialogOpen(open);
            if (!open) {
              setCurrentTestDocument(null);
            }
          }}
          document={currentTestDocument}
        />
      )}

      {/* Edit Name Dialog */}
      <Dialog open={editNameDialogOpen} onOpenChange={setEditNameDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('knowledgeBase.detail.dialogs.editName.title')}</DialogTitle>
            <DialogDescription>
              {t('knowledgeBase.detail.dialogs.editName.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <Input
              value={newKnowledgeBaseName}
              onChange={(e) => setNewKnowledgeBaseName(e.target.value)}
              placeholder={t('knowledgeBase.detail.dialogs.editName.placeholder')}
              className="w-full"
            />
          </div>
          <DialogFooter className="mt-4">
            <Button 
              variant="outline" 
              onClick={() => setEditNameDialogOpen(false)}
            >
              {t('knowledgeBase.cancel')}
            </Button>
            <Button 
              onClick={handleSaveName}
              disabled={!newKnowledgeBaseName.trim() || newKnowledgeBaseName === knowledgeBase?.name}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {t('knowledgeBase.detail.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Description Dialog */}
      <Dialog open={editDescriptionDialogOpen} onOpenChange={setEditDescriptionDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('knowledgeBase.detail.dialogs.editDescription.title')}</DialogTitle>
            <DialogDescription>
              {t('knowledgeBase.detail.dialogs.editDescription.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <Input
              value={newKnowledgeBaseDescription}
              onChange={(e) => setNewKnowledgeBaseDescription(e.target.value)}
              placeholder={t('knowledgeBase.detail.dialogs.editDescription.placeholder')}
            />
          </div>
          <DialogFooter className="mt-4">
            <Button 
              variant="outline" 
              onClick={() => setEditDescriptionDialogOpen(false)}
            >
              {t('knowledgeBase.cancel')}
            </Button>
            <Button 
              onClick={handleSaveDescription}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {t('knowledgeBase.detail.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default KnowledgeBaseDetailContent; 
